// OralReport.jsx
import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Download, Share2 } from "lucide-react";
import AnimationOnScroll from "react-animate-on-scroll";
import "animate.css/animate.min.css";
import { Link, useLocation } from "react-router-dom";
import BackArrow from "../assets/images/back-arrow.svg";
import AIIcon from "../assets/images/Ai-analytics.svg";
import Cavities from "../assets/images/Cavities.svg";
import GumHealth from "../assets/images/gum_disease.svg";
import Plaque from "../assets/images/Plaque-Control.svg";
import Whitening from "../assets/images/whitening.svg";
import Alignment from "../assets/images/orthodontist.svg";
import RecommandIcon from "../assets/images/Recommendations.svg";
import ListIcon from "../assets/images/list-icon.svg";

const riskColors = {
  "High Risk": "text-red-600 bg-red-100",
  "Moderate Risk": "text-[#ba7304] bg-[#fdf3d2]",
  "Low Risk": "text-green-600 bg-green-100",
};

const progressColor = (risk) =>
  risk === "High Risk"
    ? "bg-red-500"
    : risk === "Moderate Risk"
    ? "bg-[#f7c117]"
    : "bg-green-500";

const iconMap = {
  Cavities,
  GumHealth,
  Plaque,
  Whitening,
  Alignment,
};
const recommendations = [
  "Continue regular dental check-ups.",
  "Maintain good oral hygiene.",
  "Use antibacterial mouthwash.",
  "Floss regularly to reduce gum inflammation.",
  "Increase brushing frequency.",
];
const DentalScanHistory = () => {
  const { state } = useLocation();
  const assessmentData = state?.dentalAnalysis;
  const {
    ai_summary,
    analysis_limitations,
    cavities,
    gum_health,
    plaque,
    whitening,
    alignment,
    image_validation,
    overall_score,
    notes,
  } = assessmentData.result;

  const parameters = [
    { ...cavities, name: "Cavities", icon: "Cavities" },
    { ...gum_health, name: "Gum Health", icon: "GumHealth" },
    { ...plaque, name: "Plaque Control", icon: "Plaque" },
    { ...whitening, name: "Whitening", icon: "Whitening" },
    { ...alignment, name: "Alignment", icon: "Alignment" },
  ];

  const circleScore = {
    score: overall_score,
    confidence: image_validation.visibility_score,
    risk: "Moderate Risk",
  };
  const score = 77.0;

  const calculateRisk = (score) => {
    if (score >= 80) return "Low Risk";
    if (score >= 60) return "Moderate Risk";
    return "High Risk";
  };

  return (
    <div className="max-w-md lg:max-w-[80%] md:max-w-[80%] mx-auto">
      <div className="flex mb-5 px-6 relative z-50">
        <Link
          to="https://gitex-demo.careplix.com/UnityHealthExperienceCenter"
          className="w-9 h-9 bg-[#2D2D2D] rounded-full flex justify-center items-center"
        >
          <img src={BackArrow} alt="Back" />
        </Link>
        <div className="ml-4 -mt-1">
          <h1 className="text-lg lg:text-2xl md:text-2xl font-semibold">
            Your <span className="text-secondary">Report</span>
          </h1>
          <p className="text-xs text-white/85">
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </div>
      {/* Background Blur */}
      <div className="circle-blur w-[80%] lg:w-[60%] md:w-[60%] h-64 mx-auto mt-10 blur-[90px] absolute left-0 right-0 top-10"></div>
      {/* Overall Score */}
      <div className="px-6 w-full mb-8">
        <div className="bg-[#1D1D1D]/70 rounded-[20px] p-5 mb-4 relative z-20">
          <div className="relative w-32 h-32 mx-auto -mt-2">
            {/* Donut Chart */}
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: "score", value: circleScore.score },
                    { name: "rest", value: 100 - circleScore.score },
                  ]}
                  startAngle={90}
                  endAngle={-270}
                  innerRadius="75%"
                  outerRadius="85%"
                  dataKey="value"
                  stroke="none"
                >
                  <Cell fill="#A7D4FF" />
                  <Cell fill="#7c7c7c" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Score Text Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-bold text-xl">
                {circleScore.score}%
              </span>
            </div>
          </div>

          <h2 className=" text-center font-medium text-base mb-4">
            Overall Oral Hygiene Score
          </h2>
          <div className="flex items-center justify-center gap-4 text-xs">
            <span
              className={`px-2 text-xs py-0.5 rounded-full ${
                riskColors[circleScore.risk]
              }`}
            >
              {circleScore.risk}
            </span>
            <span className="text-white text-xs">
              {circleScore.confidence}% Confidence
            </span>
          </div>

          <div className="relative h-2 mt-5 bg-gray-200 rounded-full overflow-hidden w-5/12 mx-auto">
            <div
              className="absolute top-0 left-0 h-full bg-yellow-500"
              style={{ width: `${score}%` }}
            />
          </div>

          {/* Description */}
          <p className="text-xs font-light text-white/80 mt-3 text-center">
            <span className="font-medium"> Average of: </span>
            Cavities, Gum Health, Plaque Control, Whitening & Alignment
          </p>
        </div>
      </div>
      <div className="lg:px-6 md:px-6 px-0 w-full">
        <div className="bg-white px-6 pb-24 pt-4 lg:rounded-tl-2xl lg:rounded-tr-2xl md:rounded-tl-2xl md:rounded-tr-2xl">
          {/* AI Summary */}
          <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
            <div className="mt-5 p-4 bg-[#fdfeff] border border-[#dadeeb] rounded-xl">
              <div className="flex items-center mb-2">
                <img src={AIIcon} className="w-6 mr-2" alt="" />
                <h3 className="text-base font-medium text-primary mb-1 flex items-center gap-1">
                  AI Analysis Summary
                </h3>
              </div>

              <p className="text-gray-700 text-xs mb-3">{ai_summary}</p>
              <div className="bg-yellow-50 text-primary p-2 rounded-md text-xs border border-yellow-600">
                <span className="font-semibold mb-1">
                  <span className="text-lg text-yellow-800 font-medium mr-1">
                    ⚠
                  </span>
                  Analysis Limitations:
                </span>
                <p className="text-xs mt-1">{analysis_limitations}</p>
              </div>
            </div>
          </AnimationOnScroll>
          {/* Parameter Scores */}
          <div className="grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 lg:gap-4 md:gap-4 gap-3 pt-4">
            {parameters.map((param, i) => (
              <AnimationOnScroll
                key={i}
                animateIn="animate__fadeInUp"
                duration={0.65}
              >
                <div className="bg-[#fdfeff] border border-[#dadeeb] rounded-xl p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <span className="w-8 h-8 rounded-full bg-secondary/30 flex items-center justify-center mr-3">
                        <img
                          src={iconMap[param.icon]}
                          className="w-5"
                          alt={param.name}
                        />
                      </span>
                      <h3 className="text-sm font-medium text-primary">
                        {param.name}
                      </h3>
                    </div>
                    <span className="text-xl font-semibold text-primary">
                      {param.score}
                    </span>
                  </div>
                  <div className="h-[5px] w-full bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`${progressColor(
                        calculateRisk(param.score)
                      )} h-full`}
                      style={{ width: `${param.score}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span
                      className={`rounded-full text-xxs px-2 py-0.5 ${
                        riskColors[calculateRisk(param.score)]
                      }`}
                    >
                      {calculateRisk(param.score)}
                    </span>
                    <span className="text-xs text-primary/85">
                      {param.confidence}% Confidence
                    </span>
                  </div>
                </div>
              </AnimationOnScroll>
            ))}
          </div>

          {/* Recommendations */}
          <div className="bg-white mt-6">
            <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
              <div className="flex items-center mb-4">
                <span className="w-8 h-8 rounded-full bg-[#fff8c1] mr-3 flex items-center justify-center">
                  <img src={RecommandIcon} className="w-5" alt="" />
                </span>
                <h4 className="text-base font-medium text-primary">
                  Recommendations
                </h4>
              </div>
            </AnimationOnScroll>
            <ul className="text-sm list-disc list-inside text-gray-600 space-y-2">
              {recommendations.map((item, index) => (
                <AnimationOnScroll
                  animateIn="animate__fadeInUp"
                  duration={0.65}
                >
                  <li
                    key={index}
                    className="flex items-start space-x-2 px-2 py-3 bg-blue-50 rounded-lg text-xs text-primary"
                  >
                    <img src={ListIcon} alt="" className="w-4" />
                    <span>{item}</span>
                  </li>
                </AnimationOnScroll>
              ))}
            </ul>
            <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
              <p className="text-xs mt-3 bg-green-50 text-green-800 px-2 py-3 rounded-lg flex items-start">
                <span className="mr-2">💚</span> Remember: Dental checkups every
                6 months are essential.
              </p>
            </AnimationOnScroll>
          </div>

          {/* Actions */}
          <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
            <div className="grid lg:grid-cols-3 md:grid-cols-3 grid-cols-1 gap-4 mt-7 mb-5">
              <button className="w-full py-2 rounded-full bg-primary text-white h-12 font-medium text-sm flex justify-center items-center gap-2">
                <Download size={16} /> Download PDF Report
              </button>
              <button className="w-full py-2 rounded-full h-12 bg-gray-200 text-primary text-sm font-medium flex justify-center items-center gap-2">
                <Share2 size={16} /> Share with Dentist
              </button>
              <button className="w-full py-2 rounded-full h-12 border border-gray-300 text-primary text-sm font-medium">
                Take Another Scan
              </button>
            </div>
          </AnimationOnScroll>
        </div>
      </div>
    </div>
  );
};

export default DentalScanHistory;
